import React, { ComponentProps, JSX } from 'react';

import styled from 'styled-components';

import * as analytics from '@adsk/acs-web-analytics';
import { useUser } from '@adsk/alloy-provider';
import theme, {
  flattenStyle,
  StylableComponent,
} from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import {
  MentionData,
  MentionType,
  MENTION_ANALYTICS,
  MENTION_TYPES,
} from '../consts';
import { MentionCompanyTooltip } from './MentionTooltips/MentionCompanyTooltip';
import { MentionRoleTooltip } from './MentionTooltips/MentionRoleTooltip';
import { MentionUserTooltip } from './MentionTooltips/MentionUserTooltip';

interface StyledSpanProps {
  $isCurrentUser: boolean;
}

const StyledSpan = styled.span<StyledSpanProps>`
  ${theme.typography.bodyMediumBold};
  display: inline-block;
  cursor: pointer;
  background: ${theme.colors.charcoal100};
  color: ${theme.colors.charcoal900};
  &:hover {
    background: ${theme.colors.charcoal200};
  }
  ${({ $isCurrentUser }) =>
    $isCurrentUser && {
      background: theme.colors.yellowOrange100,
      '&:hover': {
        background: theme.colors.yellowOrange300,
      },
    }}
`;

export type CalloutMentionProps = StylableComponent<HTMLSpanElement> & {
  entityKey?: string;
  decoratedText?: string;
  mention?: MentionData;
  renderTooltip?: ({
    mention,
  }: {
    mention?: MentionData;
  }) => JSX.Element | null;
};

const CALLOUT_TOOLTIPS = {
  [MENTION_TYPES.USER]: MentionUserTooltip,
  [MENTION_TYPES.COMPANY]: MentionCompanyTooltip,
  [MENTION_TYPES.ROLE]: MentionRoleTooltip,
};

type DefaultTooltipProps = Omit<ComponentProps<typeof Tooltip>, 'content'> & {
  mention?: MentionData;
  children?: React.ReactNode;
};

export function renderDefaultTooltip({
  mention,
  children,
  ...props
}: DefaultTooltipProps) {
  const TooltipComponent =
    CALLOUT_TOOLTIPS[mention?.type?.toLocaleLowerCase() as MentionType];

  if (!mention) {
    return null;
  }

  if (!TooltipComponent) {
    const error = `renderDefaultTooltip: No mention tooltip for type ${mention?.type}`;

    console.error(error);
    analytics.track(MENTION_ANALYTICS.ERROR, { error });

    return null;
  }

  return (
    <TooltipComponent {...props} mention={mention}>
      {children}
    </TooltipComponent>
  );
}

const MentionCallout = React.forwardRef<HTMLSpanElement, CalloutMentionProps>(
  (
    {
      className,
      style,
      mention,
      children,
      entityKey, // required for draft-js internal usage
      decoratedText, // required for draft-js internal usage
      onClick,
      onMouseEnter,
      renderTooltip = renderDefaultTooltip,
      ...props
    },
    ref,
  ) => {
    const { user: currentUser } = useUser();

    return (
      <StyledSpan
        $isCurrentUser={
          mention?.type === MENTION_TYPES.USER &&
          currentUser?.autodeskId === mention?.id
        }
        ref={ref}
        className={className}
        style={flattenStyle(style)}
        onClick={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
          if (!children) {
            // report view only
            analytics.track(MENTION_ANALYTICS.VIEW_CLICK, {
              mention_type: mention?.type,
              mention_name: mention?.name,
            });
          }

          onClick?.(e);
        }}
        onMouseEnter={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
          if (!children) {
            // report view only
            analytics.track(MENTION_ANALYTICS.VIEW_HOVER, {
              mention_type: mention?.type,
              mention_name: mention?.name,
            });
          }

          onMouseEnter?.(e);
        }}
        {...props}
      >
        {renderTooltip({ mention, children, ...props })}
      </StyledSpan>
    );
  },
);

MentionCallout.displayName = 'MentionCallout';

export default MentionCallout;
